import { yupResolver } from "@hookform/resolvers/yup";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Controller, FormProvider, type SubmitHandler, useForm } from "react-hook-form";
import { useParams, createFileRoute } from "@tanstack/react-router";
import React from "react";
import { useTranslation } from "react-i18next";

import type { UpdatePasswordInput } from "../../api/forgot-password";
import { updatePasswordSchemaInput, updateNewPassword } from "../../api/forgot-password";
import HidenTextField from "../../components/HidenTextField";
import RegisterPasswordTextField from "../../components/RegisterPasswordTextField";

function Component(): React.JSX.Element {
  const { t } = useTranslation();
  const { id } = useParams({ from: "/__public/reset-password/$id" });
  const [state, setState] = React.useState<"success_loader" | "error_expired" | "update_success">("success_loader");

  const methods = useForm<UpdatePasswordInput>({
    defaultValues: { password: "", confirmPassword: "" },
    resolver: yupResolver(updatePasswordSchemaInput),
    criteriaMode: "all",
    mode: "all",
  });

  const { control, handleSubmit, formState } = methods;

  const onSubmit: SubmitHandler<UpdatePasswordInput> = async (data): Promise<void> => {
    try {
      await updateNewPassword({ ...data, id: String(id) });
      setState("update_success");
    } catch (error) {
      setState("error_expired");
    }
  };

  if (state === "update_success")
    return (
      <section>
        <Stack gap={3}>
          <Typography color="success.main" variant="h4">
            {t("resetPassword.successTitle")}
          </Typography>
          <Typography>{t("resetPassword.keepPassword")}</Typography>
          <Box style={{ width: "100%" }} textAlign="center">
            <Button fullWidth href="/login" size="large" variant="contained">
              {t("resetPassword.enterPortal")}
            </Button>
          </Box>
        </Stack>
      </section>
    );

  if (state === "error_expired")
    return (
      <section>
        <Stack gap={3}>
          <Typography color="warning.main" variant="h4">
            {t("resetPassword.expiredTitle")}
          </Typography>
          <Typography>{t("resetPassword.expiredMessage")}</Typography>
          <Typography>{t("resetPassword.requestNewLink")}</Typography>
          <Box style={{ width: "100%" }} textAlign="center">
            <Button fullWidth href="/forgot-password" size="large" variant="contained">
              {t("forgotPassword.title")}
            </Button>
          </Box>
        </Stack>
      </section>
    );

  return (
    <section>
      <FormProvider {...methods}>
        <form autoComplete="off" autoSave="off" onSubmit={(e) => void handleSubmit(onSubmit)(e)}>
          <Stack gap={3}>
            <Typography textAlign="left" variant="h4" width="100%">
              {t("resetPassword.title")}
            </Typography>
            <Typography style={{ fontWeight: "300", marginTop: "-22px" }} variant="subtitle1">
              {t("resetPassword.subtitle")}
            </Typography>
            <Typography>{t("resetPassword.keepPasswordTypo")}</Typography>
            <Controller
              control={control}
              name="password"
              render={({ field, fieldState: { error } }) => {
                const matches = (() => {
                  const result = error && error.types ? (error.types.matches as string[]) : [];
                  if (!Array.isArray(result)) return [result];
                  return result;
                })();

                return (
                  <RegisterPasswordTextField
                    {...field}
                    digitsError={matches.some((item) => item === "digitsError")}
                    error={Boolean(error)}
                    fullWidth
                    label={t("resetPassword.passwordLabel")}
                    lowercaseLetterError={matches.some((item) => item === "lowercaseLetterError")}
                    minMaxError={matches.some((item) => item === "minMaxError")}
                    specialCharError={matches.some((item) => item === "specialCharError")}
                    uppercaseLetterError={matches.some((item) => item === "uppercaseLetterError")}
                    variant="outlined"
                  />
                );
              }}
            />
            <Controller
              control={control}
              name="confirmPassword"
              render={({ field, fieldState: { error } }) => (
                <HidenTextField
                  {...field}
                  error={Boolean(error)}
                  fullWidth
                  helperText={error ? error.message : " "}
                  label={t("resetPassword.confirmPasswordLabel")}
                  variant="outlined"
                />
              )}
            />
            <Box style={{ width: "100%" }} textAlign="center">
              <Button disabled={!formState.isValid} fullWidth size="large" type="submit" variant="contained">
                {t("resetPassword.saveButton")}
              </Button>
            </Box>
          </Stack>
        </form>
      </FormProvider>
    </section>
  );
}

export const Route = createFileRoute("/__public/reset-password/$id")({
  component: Component,
});
